
import React from 'react';
import { Message } from "@/types/message";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { History } from "lucide-react";
import MessageCard from "../../MessageCard";

interface RegeneratedLatestThreadGroupProps {
  thread: {
    threadId: string;
    messages: Message[];
    latestMessage: Message;
    timestamp: Date;
    hasMultipleVersions: boolean;
  };
  companyName: string;
  onRegenerateAIResponse?: (messageId: string, tone: string, customInstructions?: string) => Promise<Message | null>;
  isRegenerating?: boolean;
  regeneratingMessageId?: string;
  orderNumber?: string;
  userEmail?: string;
  sellerEmail?: string;
  latestAIMessage?: Message;
  getTypeIndicator: (type: string) => { icon: JSX.Element, color: string };
}

const RegeneratedLatestThreadGroup = ({ 
  thread, 
  companyName, 
  onRegenerateAIResponse, 
  isRegenerating, 
  regeneratingMessageId,
  orderNumber,
  userEmail,
  sellerEmail,
  latestAIMessage, 
  getTypeIndicator
}: RegeneratedLatestThreadGroupProps) => {
  const latestMessage = thread.messages[thread.messages.length - 1];
  const olderVersions = thread.messages.slice(0, -1);
  const { color } = getTypeIndicator(latestMessage.type);
  
  return (
    <div className="mb-2 last:mb-0 relative">
      {/* Timeline connector */}
      <div className="absolute left-4 top-0 h-full w-[1px] bg-primary-foreground/20 hidden md:block" />
      
      {/* Show older versions in collapsed form */}
      {olderVersions.length > 0 && (
        <Collapsible 
          className="mb-3 rounded-lg border border-muted transition-all relative hover:border-muted-foreground/20"
          defaultOpen={false}
        >
          <div className="flex items-center p-3 bg-muted/10 rounded-t-lg">
            <div className="flex items-center flex-1 min-w-0">
              <div className="flex items-center space-x-2 text-sm">
                <History className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">Previous Versions</span>
              </div>
              
              <div className="ml-2 text-xs text-muted-foreground truncate">
                {olderVersions.length} earlier version{olderVersions.length > 1 ? 's' : ''}
              </div>
            </div>
            
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                <ChevronDown className="h-4 w-4" />
              </Button>
            </CollapsibleTrigger>
          </div>
          
          <CollapsibleContent>
            <div className="p-3 space-y-3">
              {olderVersions.map((message, messageIndex) => (
                <div key={message.id} className="pl-2 border-l-2 border-muted">
                  <MessageCard
                    message={{
                      ...message,
                      isRegenerated: true
                    }}
                    companyName={companyName}
                    isLatestAIMessage={false}
                    isLatestMessage={false}
                    orderNumber={orderNumber}
                    userEmail={userEmail}
                    sellerEmail={sellerEmail}
                    id={`message-${message.id}`}
                    showVersionInfo={true}
                    versionNumber={message.version || messageIndex + 1}
                    totalVersions={thread.messages.length}
                  />
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
      
      {/* Show latest version expanded */}
      <div className="relative">
        {/* Timeline dot for latest message */}
        <div className={`absolute left-4 top-5 w-2 h-2 rounded-full ${color} z-10 hidden md:block`} />
        
        <MessageCard
          message={{
            ...latestMessage,
            isRegenerated: Boolean(latestMessage.relatedToMessageId)
          }}
          companyName={companyName}
          onRegenerateAIResponse={latestMessage.type === "ai" ? onRegenerateAIResponse : undefined}
          isLatestAIMessage={latestAIMessage && latestMessage.id === latestAIMessage.id}
          isRegenerating={isRegenerating && regeneratingMessageId === latestMessage.id}
          orderNumber={orderNumber}
          userEmail={userEmail}
          sellerEmail={sellerEmail}
          id="latest-message"
          isLatestMessage={true}
          showVersionInfo={true}
          versionNumber={latestMessage.version || thread.messages.length}
          totalVersions={thread.messages.length}
        />
      </div>
    </div>
  );
};

export default RegeneratedLatestThreadGroup;
